import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { changelogItems, type ChangelogChange } from './changelogData';

interface ChangelogProps {
  open: boolean;
  onClose: () => void;
}

export const Changelog: React.FC<ChangelogProps> = ({ open, onClose }) => {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl h-[80vh] p-0 gap-0">
        <DialogHeader className="px-6 py-4 border-b">
          <DialogTitle>{t('changelog.title', "What's New")}</DialogTitle>
        </DialogHeader>
        <ScrollArea className="flex-1 px-6">
          <div className="py-4 space-y-6">
            {changelogItems.map((item, index) => (
              <div key={item.version} className="relative">
                {/* Timeline connector */}
                {index !== changelogItems.length - 1 && (
                  <div className="absolute left-[7px] top-[1.25rem] w-[2px] h-[calc(100%+1.5rem)] bg-border" />
                )}

                <div className="flex gap-4">
                  {/* Timeline dot */}
                  <div className="relative flex-shrink-0 w-4 h-4 mt-1">
                    <div
                      className={`absolute inset-0 rounded-full ${index === 0 ? 'bg-primary' : 'bg-border'}`}
                    />
                    {index === 0 && (
                      <div className="absolute inset-0 rounded-full bg-primary animate-pulse" />
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 pb-2">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-semibold text-sm">
                        Version {item.version}
                      </h3>
                      {index === 0 && (
                        <Badge variant="default" className="h-5 px-2 text-xs">
                          Latest
                        </Badge>
                      )}
                      <span className="text-xs text-muted-foreground">
                        {item.date}
                      </span>
                    </div>
                    <ul className="space-y-1">
                      {item.changes.map((change, changeIndex) => {
                        const isObjectChange = typeof change === 'object';
                        const changeText = isObjectChange
                          ? (change as ChangelogChange).text
                          : change;
                        const subItems = isObjectChange
                          ? (change as ChangelogChange).subItems
                          : undefined;

                        return (
                          <li key={changeIndex}>
                            <div className="text-sm text-muted-foreground leading-relaxed flex items-start gap-2">
                              <span className="text-muted-foreground/60 mt-0.5">
                                •
                              </span>
                              <span>{changeText}</span>
                            </div>
                            {subItems && subItems.length > 0 && (
                              <ul className="ml-6 mt-1 space-y-0.5">
                                {subItems.map((subItem, subIndex) => (
                                  <li
                                    key={subIndex}
                                    className="text-sm text-muted-foreground/80 leading-relaxed flex items-start gap-2"
                                  >
                                    <span className="text-muted-foreground/40 mt-0.5">
                                      ◦
                                    </span>
                                    <span>{subItem}</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default Changelog;
